import { useMemo } from 'react';
import { useAccount } from '../../contexts/account/AccountContexts';
import { useGetProject } from '../../api/hooks/project/useGetProject';
import { Addons } from '../../types/account/Addons';

export interface ModuleAccessOptions {
  /**
   * Let a full (non-limited) super admin in without the addon. Mandatory so every gate states its intent:
   * - `true`  — entitlement addons that unlock a module/tab (ADS_STATS, …): support previews the tab on any
   *             project before the client buys it. Backend mirror: `AccountService.validateAddon`.
   * - `false` — behaviour addons (NEW_STATS, PAUSE_MODE, …): a super admin must see exactly what the client
   *             sees, so only the owner's addon counts.
   */
  allowSuperAdmin: boolean;
}

export interface ModuleAccess {
  canAccess: boolean;
  /** Granted only by the super-admin role — the project owner has no addon. Mark such UI as dev-only. */
  isDevOnly: boolean;
  /** The addons the decision was made on: the project OWNER's on a client's project, else the account's. */
  effectiveAddons: Addons[];
  /** Owner addons still loading (super admin on a client's project). Don't redirect on `!canAccess` yet. */
  isLoading: boolean;
}

/**
 * Module gate = "the project owner holds the addon" OR (opt-in) "the viewer is a super admin".
 *
 * Never decide on the logged-in super admin's own addons — that shows the module only on their own
 * projects and hides it on the clients' ones they support. `project.ownerAddons` is set by the backend
 * only when a super admin fetches someone else's project; everyone else falls back to their own account.
 * Use the same hook for the menu entry and the page guard, so the two
 * never disagree.
 */
export const useModuleAccess = (
  addon: Addons,
  projectId: number | null,
  options: ModuleAccessOptions
): ModuleAccess => {
  const { accountResponse } = useAccount();
  // Same query key as the sidebar — served from the react-query cache, no extra request.
  const { project, isLoading } = useGetProject(projectId);

  return useMemo(() => {
    const effectiveAddons = (project?.ownerAddons ?? accountResponse?.addons ?? []) as Addons[];
    const ownerHasAddon = effectiveAddons.includes(addon);
    const isFullSuperAdmin = !!accountResponse?.isSuperAdmin && !accountResponse.isLimitedSuperAdmin;
    const canAccess = ownerHasAddon || (options.allowSuperAdmin && isFullSuperAdmin);
    return {
      canAccess,
      isDevOnly: canAccess && !ownerHasAddon,
      effectiveAddons,
      // Only a super admin's decision can change once the project arrives (ownerAddons); for everyone
      // else the account's addons are final, so guards must not wait for the project fetch.
      isLoading: isLoading && !!accountResponse?.isSuperAdmin,
    };
  }, [accountResponse, project?.ownerAddons, addon, options.allowSuperAdmin, isLoading]);
};
